import { Controller } from '@hotwired/stimulus';

import { crSectionLayout } from '../utils/cr_section_layout.js';

/**
 * « Sections du compte rendu » (sections par CR, plan 2026-10-27) : aperçu vif du sommaire de la
 * page de garde et compteur, quand un interrupteur de section change.
 *
 * Le serveur rend l'état initial (`MeetingReports::StructureModalComponent`, numéros par
 * `MeetingReports::SectionLayout`, seule source : verdict DS 6). Ce contrôleur ne construit rien :
 * il bascule l'attribut `hidden` et réécrit `textContent` (jamais `innerHTML`, jamais une classe).
 * `refresh()` tourne aussi au `connect()` : sur le rendu serveur il ne change rien (il n'écrit qu'une
 * valeur différente), et il rattrape un interrupteur basculé avant la connexion (retour arrière,
 * formulaire restauré par le navigateur).
 *
 * Règle (jumelle de Ruby, `utils/cr_section_layout.js`, parité testée) : visible = verrouillée OU
 * cochée ; numéro = rang parmi les visibles hors Annexes ; page = `firstPage + floor(i / linesPerPage)`
 * sur toutes les lignes visibles, Annexes comprises.
 *
 * Cibles (toutes avec `data-slug`, sauf `counter`) :
 *   - `row`        ligne de la liste ; `data-locked="true"` (Remarques, Prochaine réunion),
 *                  `data-unnumbered="true"` (Annexes)
 *   - `switch`     case à cocher de la section (valeur = slug), absente sur une ligne verrouillée
 *   - `number`     cellule du numéro de la ligne (vide quand masquée)
 *   - `badge`      enveloppe nue du badge « Masquée » (`hidden` quand la section est visible)
 *   - `line`       enveloppe nue de la ligne du sommaire de l'aperçu (`hidden` quand masquée)
 *   - `lineNumber` « N. » ou vide
 *   - `linePage`   n° de page de l'aperçu, vide quand masquée
 *   - `counter`    « N sections sur 9 » (région `role="status"`)
 *   - `subSwitch`  sous-interrupteur d'un réglage des remarques (« Remarques soldées », « Sous-lots » ;
 *                  plan 2026-11-03-cr-reglages-soldees-sous-lots, R9), sans `data-slug` : il ne nourrit
 *                  ni la numérotation, ni l'aperçu, ni le compteur ; seul `reset()` le lit
 *
 * Valeurs : `total` (9), `firstPage` (2), `linesPerPage` (3) ; `counterTemplate` facultative,
 * libellé du compteur venu du serveur avec `%{count}` et `%{total}`. Sans elle, seul le PREMIER
 * nombre du texte rendu par le serveur est remplacé : aucun libellé français n'est écrit ici.
 *
 * Action : `change->cr-structure-preview#refresh` sur chaque interrupteur.
 * Événement : `modal:open` émis par `modal` sur le même hôte → `reset()` (écouté au `connect()`, sans
 * markup) : une fenêtre fermée sans enregistrer se rouvre sur l'état serveur.
 *
 * @example
 *   <div data-controller="cr-structure-preview" data-cr-structure-preview-total-value="9"
 *        data-cr-structure-preview-first-page-value="2" data-cr-structure-preview-lines-per-page-value="3">
 *     <li data-slug="generalites" data-cr-structure-preview-target="row">
 *       <span data-slug="generalites" data-cr-structure-preview-target="number">2</span>
 *       <span data-slug="generalites" data-cr-structure-preview-target="badge" hidden>…</span>
 *       <input type="checkbox" value="generalites" data-slug="generalites" checked
 *              data-cr-structure-preview-target="switch" data-action="change->cr-structure-preview#refresh">
 *     </li>
 *     <li><fieldset><legend class="sr-only">Affichage des remarques</legend><ul><li>
 *       <input type="hidden" name="structure[remarks_show_solved]" value="0">
 *       <input type="checkbox" role="switch" name="structure[remarks_show_solved]" value="1" checked
 *              data-cr-structure-preview-target="subSwitch">
 *     </li></ul></fieldset></li>
 *     <span role="status" data-cr-structure-preview-target="counter">9 sections sur 9</span>
 *     <div data-slug="generalites" data-cr-structure-preview-target="line">
 *       <span data-slug="generalites" data-cr-structure-preview-target="lineNumber">2. </span>Généralités
 *       <span data-slug="generalites" data-cr-structure-preview-target="linePage">2</span>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    'row',
    'switch',
    'number',
    'badge',
    'line',
    'lineNumber',
    'linePage',
    'counter',
    'subSwitch',
  ];

  static values = {
    total: Number,
    firstPage: { type: Number, default: 2 },
    linesPerPage: { type: Number, default: 3 },
    counterTemplate: String,
  };

  connect() {
    this.refresh();
    // Fenêtre fermée sans enregistrer (Annuler, Échap, croix, fond) puis rouverte : on repart de l'état
    // SERVEUR. Écouté à l'OUVERTURE, pas à la fermeture : `modal:close` part au début du fondu de 200 ms et
    // l'architecte verrait les interrupteurs basculer ; et `closeBeforeCache` / `switchModal` ferment sans
    // émettre `modal:close`. `modal_controller.js#open` émet `modal:open` sur SON élément, sans bulle :
    // c'est aussi l'hôte de ce contrôleur (`StructureModalComponent#modal_data`), d'où l'écoute directe.
    this.resetHandler = this.reset.bind(this);
    this.element.addEventListener('modal:open', this.resetHandler);
  }

  disconnect() {
    this.element.removeEventListener('modal:open', this.resetHandler);
  }

  /**
   * Interrupteurs de section ET sous-interrupteurs des remarques ramenés à leur état rendu (`defaultChecked` =
   * l'attribut `checked` du serveur), puis aperçu et compteur recalculés. Rien d'autre du formulaire n'est
   * touché (pas de `form.reset()`).
   */
  reset() {
    [...this.switchTargets, ...this.subSwitchTargets].forEach((toggle) => {
      if (toggle.checked !== toggle.defaultChecked) toggle.checked = toggle.defaultChecked;
    });
    this.refresh();
  }

  refresh() {
    const switches = this.#bySlug(this.switchTargets, (element) => element.value);
    const layout = crSectionLayout(
      this.rowTargets.map((row) => {
        const slug = row.dataset.slug;
        const toggle = switches.get(slug);
        return {
          slug,
          // Une ligne non verrouillée sans interrupteur reste visible : le serveur l'a rendue ainsi.
          visible: row.dataset.locked === 'true' || !toggle || toggle.checked,
          unnumbered: row.dataset.unnumbered === 'true',
        };
      }),
      { firstPage: this.firstPageValue, linesPerPage: this.linesPerPageValue }
    );

    const numbers = this.#bySlug(this.numberTargets);
    const badges = this.#bySlug(this.badgeTargets);
    const lines = this.#bySlug(this.lineTargets);
    const lineNumbers = this.#bySlug(this.lineNumberTargets);
    const linePages = this.#bySlug(this.linePageTargets);

    layout.forEach(({ slug, visible, number, page }) => {
      setText(numbers.get(slug), number === null ? '' : String(number));
      setHidden(badges.get(slug), visible);
      setHidden(lines.get(slug), !visible);
      setText(lineNumbers.get(slug), number === null ? '' : `${number}. `);
      setText(linePages.get(slug), page === null ? '' : String(page));
    });

    this.#renderCounter(layout.filter(({ visible }) => visible).length);
  }

  #renderCounter(count) {
    if (!this.hasCounterTarget) return;

    const total = this.hasTotalValue ? this.totalValue : this.rowTargets.length;
    const counter = this.counterTarget;
    const text = this.hasCounterTemplateValue
      ? this.counterTemplateValue
          .replace('%{count}', String(count))
          .replace('%{total}', String(total))
      : counter.textContent.replace(/\d+/, String(count));
    setText(counter, text);
  }

  #bySlug(elements, fallback = () => undefined) {
    const map = new Map();
    elements.forEach((element) => {
      const slug = element.dataset.slug || fallback(element);
      if (slug && !map.has(slug)) map.set(slug, element);
    });
    return map;
  }
}

// N'écrire qu'une valeur DIFFÉRENTE : au `connect()` sur le rendu serveur, rien ne bouge (pas de
// mutation, pas d'annonce de la région `status`).
function setText(element, text) {
  if (element && element.textContent !== text) element.textContent = text;
}

function setHidden(element, hidden) {
  if (element && element.hidden !== hidden) element.hidden = hidden;
}
